import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import type { AnalysisMode } from '../../../../types'
import { nazevRozpoznavani, type ZpracovaniUctenek } from '../../hooks/useZpracovaniUctenek'

/** Kolik fotek smí jedno nahrání obsahovat - stejný limit hlídá i server. */
export const MAX_FOTEK = 10

/** Fotka vybraná k nahrání; `id` drží stabilní klíč i po přeskupení. */
export type PripravenaFotka = { id: number; file: File }

/** Účtenka připravená k nahrání - jedna nebo víc fotek v pořadí shora dolů. */
export type PripravenaUctenka = PripravenaFotka[]

let posledniId = 0

/** Přidá vybrané soubory, každý jako samostatnou účtenku - spojit je jde potom. */
export function pridejFotky(uctenky: PripravenaUctenka[], files: File[]): PripravenaUctenka[] {
    return [...uctenky, ...files.map((file) => [{ id: (posledniId += 1), file }])]
}

/** Spojí účtenku s tou před ní - fotky se připojí na konec předchozí. */
export function spojSPredchozi(uctenky: PripravenaUctenka[], index: number): PripravenaUctenka[] {
    if (index <= 0 || index >= uctenky.length) {
        return uctenky
    }
    const spojena = [...uctenky[index - 1]!, ...uctenky[index]!]
    return [...uctenky.slice(0, index - 1), spojena, ...uctenky.slice(index + 1)]
}

/**
 * Spojí vybrané fotky do jedné účtenky, i když neleží vedle sebe - na místě
 * první z nich a v pořadí, v jakém jsou v přípravě. Účtenky, ze kterých
 * fotky odešly, zůstanou se zbytkem fotek; prázdné zmizí.
 */
export function spojVybrane(uctenky: PripravenaUctenka[], ids: number[]): PripravenaUctenka[] {
    const vybrane = new Set(ids)
    const spojena = uctenky.flat().filter((fotka) => vybrane.has(fotka.id))
    if (spojena.length < 2) {
        return uctenky
    }

    const prvni = uctenky.findIndex((uctenka) => uctenka.some((fotka) => vybrane.has(fotka.id)))
    const vysledek: PripravenaUctenka[] = []
    for (const [index, uctenka] of uctenky.entries()) {
        if (index === prvni) {
            vysledek.push(spojena)
        }
        const zbytek = uctenka.filter((fotka) => !vybrane.has(fotka.id))
        if (zbytek.length > 0) {
            vysledek.push(zbytek)
        }
    }
    return vysledek
}

/** Od dané fotky dál vznikne nová účtenka (opak spojení s předchozí). */
export function oddelOdFotky(uctenky: PripravenaUctenka[], index: number, fotka: number): PripravenaUctenka[] {
    const puvodni = uctenky[index]
    if (!puvodni || fotka <= 0 || fotka >= puvodni.length) {
        return uctenky
    }
    return [...uctenky.slice(0, index), puvodni.slice(0, fotka), puvodni.slice(fotka), ...uctenky.slice(index + 1)]
}

export function odeberFotku(uctenky: PripravenaUctenka[], index: number, fotka: number): PripravenaUctenka[] {
    return uctenky
        .map((uctenka, poradi) => (poradi === index ? uctenka.filter((_, cislo) => cislo !== fotka) : uctenka))
        .filter((uctenka) => uctenka.length > 0)
}

/** Soubory a jejich rozdělení do účtenek tak, jak je chce server (`receiptIndexes`). */
export function naNahrani(uctenky: PripravenaUctenka[]): { files: File[]; receiptIndexes: number[] } {
    const fotky = uctenky.flatMap((uctenka, index) => uctenka.map((fotka) => ({ file: fotka.file, index })))
    return { files: fotky.map((fotka) => fotka.file), receiptIndexes: fotky.map((fotka) => fotka.index) }
}

export function pocetFotek(uctenky: PripravenaUctenka[]): number {
    return uctenky.reduce((soucet, uctenka) => soucet + uctenka.length, 0)
}

export type ReceiptUploadGroupingProps = {
    uctenky: PripravenaUctenka[]
    onChange: (uctenky: PripravenaUctenka[]) => void
    onAddPhotos: () => void
    /** Kolik fotek jde nahrát najednou - stejný limit hlídá server. */
    maxFotek: number
    zpracovani: ZpracovaniUctenek
    /** Rozpoznává server účtenky sám (OCR, AI, lokální model)? */
    analysisAvailable: boolean
    /** Čím server rozpoznává - do popisku volby. */
    analysisProvider?: string | null
    disabled?: boolean
}

/**
 * Příprava účtenek před nahráním.
 *
 * Dlouhá účtenka bývá vyfocená na několikrát. Tady se fotky stejné účtenky
 * spojí, aby je rozpoznávání - nebo AI asistent přes MCP - dostalo najednou
 * a vznikla z nich jedna útrata. Rozpoznávání se spustí až nahráním a dál
 * běží na pozadí, takže se o seskupení i o předání asistentovi rozhoduje
 * dřív, než cokoli začne.
 */
export function ReceiptUploadGrouping({
    uctenky,
    onChange,
    onAddPhotos,
    maxFotek,
    zpracovani,
    analysisAvailable,
    analysisProvider,
    disabled,
}: ReceiptUploadGroupingProps) {
    const fotek = pocetFotek(uctenky)
    const vse = uctenky.flat()

    // Fotky se číslují napříč všemi účtenkami - podle čísla je poznat i ve velkém náhledu.
    const poradiFotky = new Map(vse.map((fotka, index) => [fotka.id, index + 1]))
    const uctenkaFotky = new Map(uctenky.flatMap((uctenka, index) => uctenka.map((fotka) => [fotka.id, index + 1] as const)))

    // Fotky označené ke spojení; odebrané fotky z výběru samy vypadnou.
    const [oznacene, setOznacene] = useState<number[]>([])
    const vybrane = oznacene.filter((id) => poradiFotky.has(id))
    const prepniVyber = (id: number) =>
        setOznacene((predchozi) => (predchozi.includes(id) ? predchozi.filter((jine) => jine !== id) : [...predchozi, id]))
    const spojit = () => {
        onChange(spojVybrane(uctenky, vybrane))
        setOznacene([])
    }

    // Fotka zobrazená ve velkém náhledu.
    const [nahled, setNahled] = useState<number>()

    return (
        <div data-testid="priprava-uctenek">
            <p className="text-secondary small">
                Každá fotka je zatím samostatná účtenka. Fotky jedné dlouhé účtenky spojte — rozpoznávání je pak dostane
                najednou a vznikne z nich jedna útrata. Klepnutím fotku zvětšíte, tlačítkem <i className="bi bi-plus-lg" aria-hidden="true" />{' '}
                ji označíte ke spojení.
            </p>

            {vybrane.length > 0 && (
                <div
                    className="position-sticky top-0 z-2 bg-body border rounded p-2 mb-3 d-flex align-items-center justify-content-between gap-2 flex-wrap"
                    data-testid="vyber-fotek"
                >
                    <span className="small">
                        Označeno {vybrane.length === 1 ? '1 fotka' : popisFotek(vybrane.length)}:{' '}
                        {vybrane.map((id) => poradiFotky.get(id)).sort((a, b) => a! - b!).join(', ')}
                    </span>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" className="su-tappable" disabled={disabled} onClick={() => setOznacene([])}>
                            Zrušit výběr
                        </Button>
                        <Button size="sm" className="su-tappable" disabled={disabled || vybrane.length < 2} onClick={spojit}>
                            <i className="bi bi-link-45deg me-1" aria-hidden="true" />
                            Spojit do jedné účtenky
                        </Button>
                    </div>
                </div>
            )}

            {fotek > 1 && (
                <div className="d-flex gap-2 flex-wrap mb-3">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="su-tappable"
                        disabled={disabled || uctenky.length === 1}
                        onClick={() => onChange([vse])}
                    >
                        <i className="bi bi-collection me-1" aria-hidden="true" />
                        Vše je jedna účtenka
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="su-tappable"
                        disabled={disabled || uctenky.length === fotek}
                        onClick={() => onChange(vse.map((fotka) => [fotka]))}
                    >
                        <i className="bi bi-grid me-1" aria-hidden="true" />
                        Každá fotka zvlášť
                    </Button>
                </div>
            )}

            {uctenky.map((uctenka, index) => (
                <Card key={uctenka[0]!.id} className="mb-2" data-testid="pripravena-uctenka">
                    <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                            <span className="fw-semibold small">Účtenka {index + 1}</span>
                            <span className="small text-secondary">{popisFotek(uctenka.length)}</span>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                            {uctenka.map((fotka, cislo) => {
                                const poradi = poradiFotky.get(fotka.id)!
                                const oznacena = vybrane.includes(fotka.id)
                                return (
                                <div key={fotka.id} style={{ width: SIRKA_NAHLEDU }}>
                                    <div className="position-relative">
                                        <button
                                            type="button"
                                            className={`btn p-0 d-block rounded ${oznacena ? 'border border-3 border-primary' : 'border-0'}`}
                                            aria-label={`Zvětšit fotku ${poradi}`}
                                            onClick={() => setNahled(fotka.id)}
                                        >
                                            <NahledFotky file={fotka.file} popis={`Účtenka ${index + 1}, fotka ${cislo + 1}`} />
                                        </button>
                                        <span className="badge text-bg-dark position-absolute bottom-0 start-0 m-1" aria-hidden="true">
                                            {poradi}
                                        </span>
                                        <Button
                                            variant={oznacena ? 'primary' : 'light'}
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-1 rounded-circle border shadow-sm"
                                            style={{ width: '2.25rem', height: '2.25rem' }}
                                            aria-pressed={oznacena}
                                            aria-label={`Označit fotku ${poradi} ke spojení`}
                                            disabled={disabled}
                                            onClick={() => prepniVyber(fotka.id)}
                                        >
                                            <i className={`bi ${oznacena ? 'bi-check-lg' : 'bi-plus-lg'}`} aria-hidden="true" />
                                        </Button>
                                    </div>
                                    <div className="d-flex justify-content-center gap-1 mt-1">
                                        {cislo > 0 && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="su-tappable flex-fill px-0"
                                                disabled={disabled}
                                                title="Od této fotky začne nová účtenka"
                                                aria-label={`Oddělit fotku ${cislo + 1} do nové účtenky`}
                                                onClick={() => onChange(oddelOdFotky(uctenky, index, cislo))}
                                            >
                                                <i className="bi bi-scissors" aria-hidden="true" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="su-tappable flex-fill px-0"
                                            disabled={disabled}
                                            aria-label={`Odebrat fotku ${cislo + 1} z účtenky ${index + 1}`}
                                            onClick={() => onChange(odeberFotku(uctenky, index, cislo))}
                                        >
                                            <i className="bi bi-trash" aria-hidden="true" />
                                        </Button>
                                    </div>
                                </div>
                                )
                            })}
                        </div>

                        {index > 0 && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="su-tappable mt-2"
                                disabled={disabled}
                                onClick={() => onChange(spojSPredchozi(uctenky, index))}
                            >
                                <i className="bi bi-link-45deg me-1" aria-hidden="true" />
                                Spojit s účtenkou {index}
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ))}

            <div className="d-grid mb-3">
                <Button variant="outline-secondary" className="su-tappable" disabled={disabled || fotek >= maxFotek} onClick={onAddPhotos}>
                    <i className="bi bi-camera me-1" aria-hidden="true" />
                    Přidat fotky
                </Button>
                <div className="small text-secondary mt-1">
                    Najednou jde nahrát nejvýš {maxFotek} fotek — třeba postupně, jak je fotíte.
                </div>
            </div>

            {nahled !== undefined && poradiFotky.has(nahled) && (
                <VelkyNahled
                    fotky={vse}
                    id={nahled}
                    poradi={poradiFotky}
                    uctenka={uctenkaFotky}
                    oznacene={vybrane}
                    disabled={disabled}
                    onShow={setNahled}
                    onPrepniVyber={prepniVyber}
                    onClose={() => setNahled(undefined)}
                />
            )}

            {zpracovani.rezimy.length > 1 && (
                <VolbaRozpoznani
                    rezimy={zpracovani.rezimy}
                    value={zpracovani.analysisMode}
                    onChange={zpracovani.setAnalysisMode}
                    analysisAvailable={analysisAvailable}
                    analysisProvider={analysisProvider}
                    disabled={disabled}
                />
            )}
        </div>
    )
}

const POPIS_REZIMU: Record<AnalysisMode, string> = {
    SERVER: 'Rozpozná {nastroj} hned po nahrání.',
    MCP: 'Rozpoznávání v aplikaci se nespustí — účtenky přečte váš AI asistent přes MCP, až ho o to požádáte.',
    BOTH: 'Rozpozná {nastroj} a přečte je i váš AI asistent přes MCP. Ke kontrole se účtenka dostane, až budou hotové oba výsledky, a použijete ten lepší.',
}

type VolbaRozpoznaniProps = {
    rezimy: AnalysisMode[]
    value: AnalysisMode
    onChange: (rezim: AnalysisMode) => void
    analysisAvailable: boolean
    analysisProvider?: string | null
    disabled?: boolean
}

/** Kdo účtenky rozpozná - aplikace, AI asistent přes MCP, nebo oba k porovnání. */
function VolbaRozpoznani({ rezimy, value, onChange, analysisAvailable, analysisProvider, disabled }: VolbaRozpoznaniProps) {
    const nastroj = nazevRozpoznavani(analysisProvider)
    const popisky: Record<AnalysisMode, string> = {
        // Nástroj jménem („Lokální model") - u volby „obojí" je pak jasné, co se s čím porovná.
        SERVER: analysisAvailable ? nastroj.charAt(0).toUpperCase() + nastroj.slice(1) : 'Ručně',
        MCP: 'AI asistent',
        BOTH: 'Obojí',
    }
    const popis = analysisAvailable || value !== 'SERVER' ? POPIS_REZIMU[value].replace('{nastroj}', nastroj) : 'Údaje z účtenek doplníte ručně.'

    return (
        <Form.Group className="mb-3">
            <Form.Label className="d-block">Kdo účtenky rozpozná</Form.Label>
            <ToggleButtonGroup type="radio" name="kdo-rozpozna" className="w-100" value={value} onChange={onChange}>
                {rezimy.map((rezim) => (
                    <ToggleButton
                        key={rezim}
                        id={`kdo-rozpozna-${rezim.toLowerCase()}`}
                        value={rezim}
                        variant="outline-primary"
                        className="su-tappable"
                        disabled={disabled}
                    >
                        {popisky[rezim]}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Form.Text className="text-muted">{popis}</Form.Text>
        </Form.Group>
    )
}

const SIRKA_NAHLEDU = '7.5rem'

type VelkyNahledProps = {
    fotky: PripravenaFotka[]
    id: number
    poradi: Map<number, number>
    uctenka: Map<number, number>
    oznacene: number[]
    disabled?: boolean
    onShow: (id: number) => void
    onPrepniVyber: (id: number) => void
    onClose: () => void
}

/**
 * Fotka ve velkém - na náhledu v seznamu se dlouhá účtenka nedá přečíst.
 * Listuje se mezi všemi fotkami a rovnou je jde označovat ke spojení.
 */
function VelkyNahled({ fotky, id, poradi, uctenka, oznacene, disabled, onShow, onPrepniVyber, onClose }: VelkyNahledProps) {
    const index = fotky.findIndex((fotka) => fotka.id === id)
    const fotka = fotky[index]!
    const predchozi = fotky[index - 1]
    const dalsi = fotky[index + 1]
    const oznacena = oznacene.includes(id)

    useEffect(() => {
        const klavesa = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft' && predchozi) onShow(predchozi.id)
            if (event.key === 'ArrowRight' && dalsi) onShow(dalsi.id)
        }
        window.addEventListener('keydown', klavesa)
        return () => window.removeEventListener('keydown', klavesa)
    }, [predchozi, dalsi, onShow])

    return (
        <Modal show onHide={onClose} centered size="lg" fullscreen="sm-down" data-testid="velky-nahled">
            <Modal.Header closeButton closeLabel="Zavřít">
                <Modal.Title as="h3" className="h6">
                    Fotka {poradi.get(id)} z {fotky.length} · účtenka {uctenka.get(id)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center p-2">
                <ObrazekFotky file={fotka.file} popis={`Fotka ${poradi.get(id)}`} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" className="su-tappable" disabled={!predchozi} aria-label="Předchozí fotka" onClick={() => predchozi && onShow(predchozi.id)}>
                    <i className="bi bi-chevron-left" aria-hidden="true" />
                </Button>
                <Button
                    variant={oznacena ? 'primary' : 'outline-primary'}
                    className="su-tappable"
                    aria-pressed={oznacena}
                    disabled={disabled}
                    onClick={() => onPrepniVyber(id)}
                >
                    <i className={`bi ${oznacena ? 'bi-check-lg' : 'bi-plus-lg'} me-1`} aria-hidden="true" />
                    {oznacena ? 'Označeno ke spojení' : 'Označit ke spojení'}
                </Button>
                <Button variant="outline-secondary" className="su-tappable" disabled={!dalsi} aria-label="Další fotka" onClick={() => dalsi && onShow(dalsi.id)}>
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

/** Fotka v plné velikosti; PDF a nezobrazitelné formáty jen ikonou. */
function ObrazekFotky({ file, popis }: { file: File; popis: string }) {
    const url = useObjectUrl(file)
    const [chyba, setChyba] = useState(false)

    if (!url || chyba) {
        return (
            <div className="py-5 text-secondary">
                <i className={`bi ${jePdf(file) ? 'bi-file-earmark-pdf' : 'bi-image'} fs-1 d-block mb-2`} aria-hidden="true" />
                {file.name}
            </div>
        )
    }
    return <img src={url} alt={popis} className="img-fluid rounded" style={{ maxHeight: '75vh', objectFit: 'contain' }} onError={() => setChyba(true)} />
}

function jePdf(file: File): boolean {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

/** Adresa vybraného souboru pro <img>; po odebrání se uvolní. PDF prohlížeč jako obrázek neukáže. */
function useObjectUrl(file: File): string | undefined {
    const [url, setUrl] = useState<string>()
    const pdf = jePdf(file)

    useEffect(() => {
        if (pdf) {
            setUrl(undefined)
            return
        }
        const adresa = URL.createObjectURL(file)
        setUrl(adresa)
        return () => URL.revokeObjectURL(adresa)
    }, [file, pdf])

    return url
}

/**
 * Náhled vybrané fotky ještě před nahráním. PDF a formáty, které prohlížeč
 * neumí zobrazit (HEIC mimo Safari), dostanou jen ikonu.
 */
function NahledFotky({ file, popis }: { file: File; popis: string }) {
    const url = useObjectUrl(file)
    const [chyba, setChyba] = useState(false)
    const pdf = jePdf(file)

    // Na výšku - účtenky jsou úzké a dlouhé, ať je vidět co nejvíc.
    const rozmer = { width: SIRKA_NAHLEDU, height: '10rem' }

    if (pdf || chyba || !url) {
        return (
            <div
                className="rounded border bg-body-tertiary d-flex align-items-center justify-content-center text-secondary"
                style={rozmer}
                title={file.name}
                aria-label={popis}
                role="img"
            >
                <i className={`bi ${pdf ? 'bi-file-earmark-pdf' : 'bi-image'} fs-3`} aria-hidden="true" />
            </div>
        )
    }

    return <img src={url} alt={popis} className="rounded border" style={{ ...rozmer, objectFit: 'cover' }} onError={() => setChyba(true)} />
}

/** „1 fotka", „2 fotky", „5 fotek". */
export function popisFotek(pocet: number): string {
    if (pocet === 1) {
        return '1 fotka'
    }
    return pocet >= 2 && pocet <= 4 ? `${pocet} fotky` : `${pocet} fotek`
}
